/* eslint-disable */
import AccordionButton from 'components/commercetools-ui/atoms/accordion';
import Popup from 'components/commercetools-ui/atoms/popup';
import Typography from 'components/commercetools-ui/atoms/typography';
import React, { FC, useState } from 'react';
import Rooms from '../rooms';
import Button from 'components/commercetools-ui/atoms/button';
import { ShoppingList } from '../types';
import { useAccount, useProjectBuilder } from 'frontastic';
import RegisterPopup from '../../authentication/register/register-popup';
import Spinner from '../../spinner';
import { useFormat } from 'helpers/hooks/useFormat';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { PROJECT_PRODUCTS_UPDATED } from 'helpers/constants/events';

interface IProps {
  open: boolean;
  onCancel: any;
  productToAdd: any;
}
const AddProductToProject: FC<IProps> = ({ open, onCancel, productToAdd }) => {
  const { translate } = useTranslation();
  const { loggedIn, account } = useAccount();
  const { locale } = useParams();
  const { formatMessage } = useFormat({ name: 'success' });

  const [selectedProjects, setSelectedProjects] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { shoppingLists, updateShoppingList } = useProjectBuilder();

  // Handle: Add or update line items in shopping list.
  const handleRoomSelection = ({ project, roomLabel, existingItem }: any) => {
    setSelectedProjects((prevSelectedProjects: any) => {
      const projectIndex = prevSelectedProjects.findIndex(
        (selectedProject: any) => selectedProject.shoppingListId === project.shoppingListId,
      );

      if (projectIndex === -1) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId: project.shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                ...(!!existingItem?.id
                  ? { lineItemId: existingItem?.id }
                  : {
                      roomNotes: '',
                      quantity: 1,
                    }),
              },
            ],
          },
        ];
      }

      const selectedProject = prevSelectedProjects[projectIndex];
      const roomIndex = selectedProject.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

      if (roomIndex === -1) {
        // Room not found: Add a new room
        const updatedProject = {
          ...selectedProject,
          selectedRooms: [
            ...selectedProject.selectedRooms,
            {
              roomLabel,
              ...(!!existingItem?.id
                ? { lineItemId: existingItem?.id }
                : {
                    roomNotes: '',
                    quantity: 1,
                  }),
            },
          ],
        };
        return [
          ...prevSelectedProjects.slice(0, projectIndex),
          updatedProject,
          ...prevSelectedProjects.slice(projectIndex + 1),
        ];
      }

      // Room found: Update if lineItemId is provided or remove otherwise
      const updatedRooms = selectedProject.selectedRooms
        .map((r: any, idx: number) =>
          idx === roomIndex
            ? !!existingItem?.id
              ? { ...(r.quantity ? { roomLabel: r.roomLabel, lineItemId: r.lineItemId } : { ...r, quantity: existingItem?.quantity}) }
              : null
            : r,
        )
        .filter(Boolean);

      const updatedProject = { ...selectedProject, selectedRooms: updatedRooms };
      return [
        ...prevSelectedProjects.slice(0, projectIndex),
        updatedRooms.length > 0 ? updatedProject : null,
        ...prevSelectedProjects.slice(projectIndex + 1),
      ].filter(Boolean);
    });
  };

  const handleAddNote = ({ shoppingListId, roomLabel, note, existingItem }: any) => {
    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex(
        (project: any) => project.shoppingListId === shoppingListId
      );

      if (projectIndex === -1) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                roomNotes: note,
                ...(existingItem ? { lineItemId: existingItem.id, quantity: existingItem.quantity } : {}),
              },
            ],
          },
        ];
      }

      const updatedProjects = prevSelectedProjects.map((project: any, index: number) => {
        if (index === projectIndex) {
          const roomIndex = project.selectedRooms.findIndex(
            (room: any) => room.roomLabel === roomLabel
          );

          if (roomIndex === -1) {
            return {
              ...project,
              selectedRooms: [
                ...project.selectedRooms,
                {
                  roomLabel,
                  roomNotes: note,
                  ...(existingItem ? { lineItemId: existingItem.id} : {}),
                },
              ],
            };
          }

          return {
            ...project,
            selectedRooms: project.selectedRooms.map((room: any, i: number) =>
              i === roomIndex ? { ...room, roomNotes: note } : room
            ),
          };
        }
        return project;
      });

      return updatedProjects;
    });
  };

  const handleDecrement = (shoppingListId: string, roomLabel: any, existingItem?: any) => {
    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex((project) => project.shoppingListId === shoppingListId);

      if (projectIndex !== -1) {
        const updatedProjects = prevSelectedProjects.map((project) => {
          if (project.shoppingListId === shoppingListId) {
            const roomIndex = project.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

            if (roomIndex !== -1) {
              const updatedRooms = project.selectedRooms
                .map((r: any) => {
                  if (r.roomLabel === roomLabel) {
                    const currentQuantity = r.quantity || existingItem?.quantity || 1;
                    const updatedQuantity = currentQuantity - 1;

                    // If the quantity goes to 0 and existingItem is provided, add room without quantity
                    if (updatedQuantity === 0 && existingItem) {
                      return { roomLabel, lineItemId: existingItem.id };
                    }

                    // If quantity goes to 0 without existingItem, remove the room
                    return updatedQuantity > 0 ? { ...r, quantity: updatedQuantity } : null;
                  }
                  return r;
                })
                .filter(Boolean);

              return { ...project, selectedRooms: updatedRooms };
            } else {
              if (existingItem) {
                const newRoom = {
                  roomLabel,
                  lineItemId: existingItem.id,
                  quantity: existingItem.quantity - 1 > 0 ? existingItem.quantity - 1 : undefined,
                };

                return {
                  ...project,
                  selectedRooms: [...project.selectedRooms, { ...newRoom }].filter(
                    (r) => r.quantity !== 0 || !r.quantity,
                  ),
                };
              }
            }
          }
          return project;
        });

        return updatedProjects.filter((project) => project.selectedRooms.length > 0);
      }

      // If the project is not found but an existingItem is provided, create a new project with remove room action
      if (existingItem) {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                lineItemId: existingItem.id,
                quantity: existingItem.quantity - 1 > 0 ? existingItem.quantity - 1 : undefined,
              },
            ].filter((r) => r.quantity !== 0 || !r.quantity),
          },
        ];
      }

      return prevSelectedProjects;
    });
  };

  const handleIncrement = (shoppingListId: string, roomLabel: any, existingItem?: any) => {
    setSelectedProjects((prevSelectedProjects: any[]) => {
      const projectIndex = prevSelectedProjects.findIndex((project) => project.shoppingListId === shoppingListId);

      if (projectIndex !== -1) {
        const updatedProjects = prevSelectedProjects.map((project) => {
          if (project.shoppingListId === shoppingListId) {
            const roomIndex = project.selectedRooms.findIndex((r: any) => r.roomLabel === roomLabel);

            if (roomIndex !== -1) {
              const updatedRooms = project.selectedRooms.map((r: any) =>
                r.roomLabel === roomLabel ? { ...r, quantity: (r.quantity || 1) + 1 } : r,
              );
              return { ...project, selectedRooms: updatedRooms };
            } else {
              return {
                ...project,
                selectedRooms: [
                  ...project.selectedRooms,
                  {
                    roomLabel,
                    ...(existingItem?.id
                      ? { lineItemId: existingItem.id, quantity: existingItem.quantity + 1 }
                      : { quantity: 1 }),
                  },
                ],
              };
            }
          }
          return project;
        });

        return updatedProjects;
      } else {
        return [
          ...prevSelectedProjects,
          {
            shoppingListId,
            selectedRooms: [
              {
                roomLabel,
                ...(existingItem?.id
                  ? { lineItemId: existingItem?.id, quantity: existingItem.quantity + 1 }
                  : { quantity: 1 }),
              },
            ],
          },
        ];
      }
    });
  };

  const handleSubmit = async () => {
    if (!selectedProjects) return;
    try {
      setLoading(true);
      const promises = selectedProjects.map((p: any) =>
        updateShoppingList({
          shoppingListId: p.shoppingListId,
          sku: productToAdd?.sku ?? '',
          items: p.selectedRooms?.map((r: any) => ({
            ...(r?.lineItemId ? { lineItemId: r.lineItemId } : {}),
            roomName: r.roomLabel,
            ...(r?.quantity ? { quantity: r.quantity } : {}),
            ...(r?.roomNotes !== undefined ? { roomNotes: r.roomNotes } : {}),
          })),
        }),
      );
      const responses = await Promise.all(promises);
      if (responses.length > 0 && responses[0]?.shoppingList?.id) {
        setSuccess(true);
        setTimeout(() => {
          setError('');
          setSuccess(false);
          setSelectedProjects([]);
          onCancel();
        }, 2000);
        if (typeof window === "undefined") return;
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: PROJECT_PRODUCTS_UPDATED,
          userId: account?.accountId,
        });
      } else {
        console.info('TODO!');
      }
    } catch (error: any) {
      const errorMessage = (error?.message || error?.toString() || 'Something went wrong!').replace('Error: ', '');

      if (errorMessage == 'Failed to fetch') {
        setError(translate(`error.no.internet`));
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedProjects([]);
    setError('');
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <>
      {!loggedIn || !account?.companyName ? (
        <RegisterPopup
          showPopup={open && (!loggedIn || !account?.companyName)}
          setShowPopup={() => {}}
          defaultLoginModal={true}
          handleModalCancel={onCancel}
        />
      ) : (
        <div>
          <Popup isOpen={open && !shoppingLists?.loading} bodyClassNames=" mx-20 sm:mx-unset  max-w-[535px]">
            <Typography className="pl-32 pt-32 text-18 font-medium text-black">
              {`Add ${productToAdd?.name ?? ''} to the following projects`}
            </Typography>
            <div className="max-h-[43vh] md:max-h-[54vh] lg:max-h-[68vh] overflow-y-auto px-32">
              {shoppingLists?.data?.results?.length === 0 && !shoppingLists?.loading && (
                <div className="pt-16 text-14 text-[#666666]">
                  No projects available.{' '}
                  <Link href={`/${locale}/project-builder/create`}>
                    <span className="underline">Click here</span>
                  </Link>{' '}
                  to create a new project.
                </div>
              )}
              {open &&
                !shoppingLists?.loading &&
                (shoppingLists?.data?.results ?? [])?.map((project: ShoppingList) => {
                  return (
                    <div className="mt-19" key={project.shoppingListId}>
                      <AccordionButton
                        closedSectionTitle={project.projectName}
                        className="border-t border-[#DDDDDD]"
                        buttonClassName="px-12 py-16 text-16 font-normal text-[#090909] text-left "
                        panelClassName="text-14 2xl:text-16 text-[#090909] font-normal"
                        iconClassName="w-20 h-20 stroke-2 text-[#323C43]"
                      >
                        <Rooms
                          project={project}
                          handleDecrement={handleDecrement}
                          handleIncrement={handleIncrement}
                          selectedProjects={selectedProjects}
                          handleRoomSelection={handleRoomSelection}
                          handleAddNote={handleAddNote}
                          productToAdd={productToAdd}
                        />
                      </AccordionButton>
                    </div>
                  );
                })}
            </div>
            <div className='px-32 pb-32'>
              {success && !error && (
                <Typography className="!mt-20 text-14 text-green-600">
                  {formatMessage({
                    id: 'product.added',
                    defaultMessage: 'Product has been successfully added.',
                  })}
                </Typography>
              )}
              {error && <span className="padding-l-4 font-medium text-error-600 text-12">{error}</span>}
              <div className="flex flex-col sm:flex-row items-center gap-24 pt-24 sm:gap-32 ">
                <Button
                  disabled={selectedProjects?.length === 0 || loading}
                  onClick={() => handleSubmit()}
                  className={
                    'border-1 z-10 w-full rounded-none border !border-[#2B2C39] bg-[#2B2C39] px-25 py-13 shadow-sm hover:!bg-[#394A54] disabled:!border-neutral-400 hover:disabled:!bg-neutral-400 md:w-fit'
                  }
                >
                  <Typography as="span" className={`text-14 font-medium text-[#fff]`}>
                    Save and continue
                  </Typography>
                </Button>
                <Button
                  onClick={handleCancel}
                  className={
                    'border-1 z-10 w-full rounded-none border !border-[#2B2C39] bg-white px-25 py-13 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit'
                  }
                >
                  <Typography as="span" className={`text-14 font-medium text-[#090909]`}>
                    Cancel
                  </Typography>
                </Button>
              </div>
            </div>
          </Popup>
        </div>
      )}
      {(loading || (shoppingLists?.loading && productToAdd)) && <Spinner />}
    </>
  );
};

export default AddProductToProject;
